/** Sessão do aluno (Zustand).
 *
 * O access token vive **em memória** (ADR-013: o leitor React usa bearer, não
 * cookie). O refresh token fica em `localStorage` para sobreviver ao reload —
 * é o mesmo trade-off do PWA offline da Fase 05.
 */
import { create } from 'zustand'

import type { CurrentUser } from '@/types/api'

const REFRESH_KEY = 'lumina.refresh'

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: CurrentUser | null
  setTokens: (access: string, refresh: string) => void
  setUser: (user: CurrentUser | null) => void
  clear: () => void
}

export function readStoredRefresh(fallback: string | null = null): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY)
  } catch {
    return fallback
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  refreshToken: readStoredRefresh(),
  user: null,
  setTokens: (access, refresh) => {
    try {
      localStorage.setItem(REFRESH_KEY, refresh)
    } catch {
      /* modo privado / storage bloqueado: a sessão só não sobrevive ao reload */
    }
    set({ accessToken: access, refreshToken: refresh })
  },
  setUser: (user) => set({ user }),
  clear: () => {
    try {
      localStorage.removeItem(REFRESH_KEY)
    } catch {
      /* idem */
    }
    set({ accessToken: null, refreshToken: null, user: null })
  },
}))

// Propagar saída e manter o refresh atualizado entre abas.
window.addEventListener('storage', event => {
  if (event.key !== REFRESH_KEY && event.key !== null) return
  if (!event.newValue) useAuthStore.setState({ accessToken: null, refreshToken: null, user: null })
  else useAuthStore.setState({ refreshToken: event.newValue })
})
